import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { urlsBuilderContextToken } from "@/context_tokens";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsTranslationsQuery from "@apps/reviews/queries/ReviewsTranslationsQuery";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import ReviewsTranslationsService from "@apps/reviews/services/ReviewsTranslationsService";
import { reviewsIconCollectionManagerToken } from "@apps/reviews/tokens";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import ReviewsGalleryManager from "./ReviewsGalleryManager";
import ReviewsGalleryWidgetComponent from "./ReviewsGalleryWidget.svelte";
import { ReviewsGalleryWidgetConfigInterface } from "./ReviewsGalleryWidgetConfig";

@injectable()
class ReviewsGalleryWidget implements WidgetInterface {
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly reviewsGalleryManager: ReviewsGalleryManager;
    private readonly reviewsTranslationsService: ReviewsTranslationsService;
    private readonly reviewsTranslationsQuery: ReviewsTranslationsQuery;

    constructor(
        private readonly config: ReviewsGalleryWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly dynamicImportManager: DynamicImportManager
    ) {
        this.logger.debug("ReviewsGalleryWidget is initiaized", config);

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );

        // ReviewsGalleryManager
        this.reviewsGalleryManager = container.resolve(ReviewsGalleryManager);

        this.reviewsTranslationsService = container.resolve(
            ReviewsTranslationsService
        );
        this.reviewsTranslationsQuery = container.resolve(
            ReviewsTranslationsQuery
        );
    }

    async init(): Promise<this> {
        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "reviews.ReviewsGalleryWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.ReviewsGalleryWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "reviews.critical.ReviewsGalleryWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "reviews.critical.ReviewsGalleryWidget"
            )
        );

        const element = this.config.target;
        const customizationManager = container.resolve(CustomizationManager);

        let placeholder = element.querySelector<HTMLElement>(
            ".gw-reviews-gallery-widget-placeholder"
        );

        if (!placeholder) {
            placeholder = document.createElement("div");
            placeholder.classList.add("gw-reviews-gallery-widget-placeholder");
            placeholder.style.display = "block";
            element.appendChild(placeholder);
        }

        const shadowRoot =
            placeholder.shadowRoot ||
            placeholder.attachShadow({ mode: "open" });

        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_GALLERY_MODAL_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );
        contextMap.set(
            urlsBuilderContextToken,
            container.resolve(CommonModule).exports.urlsBuilder
        );

        new ReviewsGalleryWidgetComponent({
            target: shadowRoot,
            props: {
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                reviewsGalleryManager: this.reviewsGalleryManager,
                reviewsTranslationsQuery: this.reviewsTranslationsQuery,
                reviewsTranslationsService: this.reviewsTranslationsService,
                dynamicImportManager: this.dynamicImportManager,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
            context: contextMap,
        });

        return this;
    }
}

export default ReviewsGalleryWidget;
